<template>
	<div>
		<LuckyPackAutoComplete v-model="displayedPack" :disable-extra-input="false" @reselect="reopenDialog" />
		<RunAgentDialog
			v-model="showDialog"
			:orchestrator="scheduledPackInput"
			mode="collect"
			@input-collected="onInputCollected"
			@update:model-value="onDialogUpdate" />
	</div>
</template>

<script setup lang="ts">
	import { computed, ref, watch } from "vue";
	import LuckyPackAutoComplete from "@/components/formfields/LuckyPackAutoComplete.vue";
	import RunAgentDialog from "./dialogs/RunAgentDialog.vue";
	import { useScheduledPackInput, type ScheduledPackSelection } from "./composables/useScheduledPackInput";
	import type { PackWithUi } from "./types";

	const scheduledPackInput = useScheduledPackInput();

	const internalPack = ref<PackWithUi | null>(null);
	const showDialog = ref(false);
	const confirmedSelection = ref<ScheduledPackSelection | null>(null);
	// The completed selection a reopened dialog was editing, so backing out of that edit restores it.
	let selectionBeforeReopen: ScheduledPackSelection | null = null;
	let selectionSequence = 0;
	let skipNextInternalClearReset = false;

	const emit = defineEmits<{
		"update:modelValue": [value: ScheduledPackSelection | null];
		"update:processing": [value: boolean];
		"update:userInputPending": [value: boolean];
	}>();

	const props = defineProps<{
		modelValue?: ScheduledPackSelection | null;
	}>();

	const displayedPack = computed({
		get: () => internalPack.value ?? props.modelValue?.pack ?? null,
		set: (pack: PackWithUi | null) => {
			if (!pack && !internalPack.value && props.modelValue) reset();
			else internalPack.value = pack;
		}
	});

	watch(
		() => props.modelValue,
		(newVal) => {
			if (newVal === null || newVal === undefined) {
				internalPack.value = null;
				confirmedSelection.value = null;
			}
		}
	);

	function setProcessing(value: boolean) {
		emit("update:processing", value);
	}

	watch(internalPack, async (newPack, oldPack) => {
		const currentSelectionSequence = ++selectionSequence;
		if (!newPack) {
			if (skipNextInternalClearReset) {
				skipNextInternalClearReset = false;
				return;
			}
			reset();
			return;
		}
		skipNextInternalClearReset = false;

		if (newPack.id === oldPack?.id) {
			return;
		}

		setProcessing(true);
		confirmedSelection.value = null;
		emit("update:userInputPending", false);
		const restoredSelection = props.modelValue?.pack?.id === newPack.id ? props.modelValue : null;

		// Always fetch full pack details since the list endpoint may not include task types
		await scheduledPackInput.loadPackDetails(newPack);
		if (currentSelectionSequence !== selectionSequence) return;

		if (!scheduledPackInput.selectedPack.value) {
			internalPack.value = null;
			confirmedSelection.value = null;
			setProcessing(false);
			emit("update:modelValue", null);
			return;
		}
		if (restoredSelection) {
			scheduledPackInput.selectedTaskIds.value = [...(restoredSelection.taskIds ?? [])];
			for (const [key, data] of Object.entries(restoredSelection.userInputData ?? {})) {
				scheduledPackInput.userInputComposable.handleUserInputUpdate(key, data);
			}
		}

		if (scheduledPackInput.needsDialog.value) {
			showDialog.value = true;
		} else {
			const data = scheduledPackInput.getCollectedData();
			selectionBeforeReopen = null;
			confirmedSelection.value = data;
			setProcessing(false);
			emit("update:modelValue", data);
		}
	});

	function onInputCollected() {
		selectionBeforeReopen = null;
		const data = scheduledPackInput.getCollectedData();
		confirmedSelection.value = data;
		setProcessing(false);
		emit("update:userInputPending", false);
		emit("update:modelValue", data);
	}

	function reopenDialog(pack?: PackWithUi) {
		if (internalPack.value && scheduledPackInput.selectedPack.value && !scheduledPackInput.loadingPack.value) {
			selectionBeforeReopen = confirmedSelection.value;
			confirmedSelection.value = null;
			showDialog.value = true;
		} else if (!internalPack.value && (pack || props.modelValue?.pack)) {
			// A selection restored after navigation reopens through the load path; keep it to restore too.
			selectionBeforeReopen = props.modelValue?.pack ? props.modelValue : null;
			internalPack.value = pack || props.modelValue?.pack || null;
		}
	}

	function reset() {
		selectionSequence++;
		selectionBeforeReopen = null;
		if (internalPack.value) skipNextInternalClearReset = true;
		internalPack.value = null;
		showDialog.value = false;
		confirmedSelection.value = null;
		scheduledPackInput.reset();
		setProcessing(false);
		emit("update:userInputPending", false);
		emit("update:modelValue", null);
	}

	defineExpose({ reopenDialog, reset });

	function onDialogUpdate(visible: boolean) {
		showDialog.value = visible;

		if (!visible) {
			setProcessing(false);
			const previousSelection = selectionBeforeReopen;
			selectionBeforeReopen = null;

			if (!confirmedSelection.value) {
				// LSP-21386: backing out of the input step without Done must not leave the Spark selected
				// with inputs the user never supplied. Re-editing a completed selection and backing out
				// keeps that completed selection (and its values); a first-time selection is cleared.
				if (previousSelection) {
					for (const [key, data] of Object.entries(previousSelection.userInputData ?? {})) {
						scheduledPackInput.userInputComposable.handleUserInputUpdate(key, data);
					}
					confirmedSelection.value = previousSelection;
					emit("update:userInputPending", false);
					emit("update:modelValue", previousSelection);
				} else {
					reset();
				}
			}
		}
	}
</script>
